import { Button, Divider, Flex, Form, Row, Spin, Typography } from "antd";
import { CfdiAssign } from "common/components/CfdiAssign";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { useDateRange } from "context/DateRangeContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Search from "antd/es/input/Search";
import {
  EXPORT_REPORT_PERMISSION,
  getPagePermissions,
} from "security/permissions";
import useLoggedUser from "hooks/useLoggedUser";
import {
  genereteReports1,
  getReceptions,
  RequestComplement,
  ResponseEntryDetail,
} from "api/generated";
import { useClient } from "hooks/useClient";
import DistributionPointSelected from "context/MyDistributionPoint";
import { querySerializer } from "common/helpers/queryUtils";
import ReceptionTable from "./components/ReceptionsTable";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useTableSelection } from "hooks/commons/useTableSelection";
import { saveJsonToFile } from "routes/stockControl/components/jsonViewer";
import DeleteComplement from "routes/cfdiComplements/DeleteComplement";
import ComplementsDrawer from "routes/cfdiComplements/ComplementsDrawer";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";

function ReceptionScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [isLoading, setIsLoading] = useState(false);

  const selection = useTableSelection();
  const [filterCfdi, setFilterCfdi] = useState<boolean>();
  const pagination = usePaginationTable<ResponseEntryDetail>(props, {
    initialSort: ["endDate,DESC"],
    persistInUrl: true,
  });

  const [showCFDIAssing, setShowCFDIAssing] = useState(false);
  const [complementToDelete, setComplementToDelete] =
    useState<RequestComplement>();
  const [complementInfo, setComplementInfo] = useState<ResponseEntryDetail>();

  const hasSelected = useMemo(
    () => selection.anySelected,
    [selection.anySelected]
  );

  const query = useMemo(
    () =>
      pagination.debouncedFilterText.length > 1
        ? pagination.debouncedFilterText
        : undefined,
    [pagination.debouncedFilterText]
  );

  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const { permissions } = useLoggedUser();
  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions
  );

  function downloadReporte() {
    if (distributionPoint) {
      setIsLoading(true);
      genereteReports1({
        client,
        body: {
          inputRegisterStartDate: startDate,
          inputRegisterEndDate: endDate,
          query,
          distributionPointId: distributionPoint?.id,
          filterCfdi,
        },
        path: {
          type: "csv",
        },
      })
        .then(({ data }) => {
          saveJsonToFile(data, "text/csv", "receptions.csv");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  const reloadTable = useCallback(async () => {
    if (!distributionPoint) {
      return;
    }
    setIsLoading(true);
    getReceptions({
      client,
      querySerializer: querySerializer,
      query: {
        request: {
          inputRegisterStartDate: startDate,
          inputRegisterEndDate: endDate,
          query,
          distributionPointId: distributionPoint.id,
          filterCfdi: filterCfdi,
        },
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    client,
    startDate,
    endDate,
    query,
    distributionPoint,
    pagination.currentPage,
    pagination.pageSize,
    pagination.sort,
    filterCfdi,
    pagination.setTotalItems,
    pagination.setItems,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  // Reset cuando cambian filtros principales
  useEffect(() => {
    pagination.resetPagination();
    selection.resetSelected();
  }, [distributionPoint?.id, startDate, endDate]);

  return (
    <Spin size="large" tip="Cargando..." spinning={isLoading}>
      <div>
        <Form layout="vertical">
          <Row gutter={16} align="bottom">
            <ResponsiveCol>
              <FormItem label="Seleccionar periodo">
                <DateRangePicker
                  config={{
                    allowedTypes: [
                      DateFilterType.DAY,
                      DateFilterType.MONTH,
                      DateFilterType.RANGE,
                    ],
                    defaultType: DateFilterType.MONTH,
                    showQuickFilters: true,
                  }}
                  rangeTimeValue={rangeTimeValue}
                  setRangeTimeValue={setRangeTimeValue}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol>
              <FormItem label="Búsqueda por número de registro, Folio, Serie, CFDI">
                <Search
                  onChange={(event) => {
                    pagination.setFilterText(event.target.value);
                  }}
                  value={pagination.filterText}
                />
              </FormItem>
            </ResponsiveCol>
            <ResponsiveCol>
              <FormItem style={{ width: "100%" }}>
                <Button
                  style={{ width: "100%" }}
                  onClick={downloadReporte}
                  disabled={!exportReportsPermissions}
                >
                  Descargar CSV
                </Button>
              </FormItem>
            </ResponsiveCol>
          </Row>
        </Form>

        <Divider style={{ marginTop: "10px" }} />
        <div style={{ margin: 16 }}>
          <span style={{ color: "#1B95CA" }}>{`Recepciones`}</span>
        </div>
        <Flex align="center" gap="middle">
          <Button
            type="primary"
            onClick={() => {
              setShowCFDIAssing(true);
            }}
            disabled={!hasSelected}
          >
            Asignar CFDI
          </Button>

          {hasSelected && (
            <Typography.Text>
              Se {selection.countSelected == 1 ? " ha" : " han"} seleccionado{" "}
              {selection.countSelected}
              {selection.countSelected == 1 ? " Recepción" : " Recepciones"}
            </Typography.Text>
          )}
        </Flex>
        <Divider />
        <ReceptionTable
          selection={selection}
          isLoading={isLoading}
          reloadTable={reloadTable}
          pagination={pagination}
          setFilterCfdi={setFilterCfdi}
          deleteComplement={(record) => {
            setComplementToDelete(record);
          }}
          goToEditComplement={(record) => {
            setComplementInfo(record);
          }}
        />

        {showCFDIAssing && (
          <CfdiAssign
            open={showCFDIAssing}
            onCancel={() => {
              setShowCFDIAssing(false);
            }}
            selectedKeys={selection.selectedKeys}
            onAssigned={() => {
              setShowCFDIAssing(false);
              selection.resetSelected();
              reloadTable();
            }}
            type="compra"
          />
        )}

        <DeleteComplement
          complement={complementToDelete}
          onCancel={(reload) => {
            setComplementToDelete(undefined);
            if (reload) {
              reloadTable();
            }
          }}
        />
        <Divider />
      </div>

      {complementInfo && (
        <ComplementsDrawer
          complement={complementInfo}
          onReturn={(reload) => {
            setComplementInfo(undefined);
            if (reload) {
              reloadTable();
            }
          }}
        />
      )}
    </Spin>
  );
}

export default withRouter(ReceptionScreen);
